import { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, TextField, CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel, Grid } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import ReactMarkdown from 'react-markdown';


const modalStyle = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)', width: 600,
    bgcolor: 'background.paper', border: '2px solid #000',
    boxShadow: 24, p: 4,
};

const timezones = ['PDT', 'MST', 'CST', 'EST'];

export default function ModifyHotTopicModal({ open, onClose, hotTopic, customer, onSubmit }) {
    const [topicId, setTopicId] = useState('');
    const [content, setContent] = useState('');
    const [validFrom, setValidFrom] = useState(null);
    const [validTill, setValidTill] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open && hotTopic) {
            setTopicId(hotTopic.id || '');
            setContent(hotTopic.content || '');
            // Attempt to parse the formatted date string back into a Date object
            setValidFrom(hotTopic.validFrom && hotTopic.validFrom !== 'N/A' ? new Date(hotTopic.validFrom) : null);
            setValidTill(hotTopic.validTill && hotTopic.validTill !== 'N/A' ? new Date(hotTopic.validTill) : null);
            setError('');
        }
    }, [open, hotTopic]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!topicId || !content || !validFrom || !validTill) {
            setError('All fields are required.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        const payload = {
            hot_topic_id: topicId,
            client_name: customer.ClientName, // <-- Added client_name
            content: content,
            valid_from: validFrom.toISOString(),
            valid_till: validTill.toISOString(),
            last_updated_by: 'x-placeholder-user',
            timestamp: new Date().toISOString() // <-- Added current timestamp
        };
        try {
            await onSubmit(payload);
        } catch (e) {
            setError(e.message || 'An unexpected error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
                    <Typography variant="h6" component="h2">Modify Hot Topic</Typography>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Hot Topic ID</InputLabel>
                        <Select value={topicId} label="Hot Topic ID" onChange={(e) => setTopicId(e.target.value)} disabled>
                            <MenuItem value={hotTopic?.id}>{hotTopic?.id}</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField label="Client Name" value={customer ? customer.ClientName : ''} fullWidth disabled sx={{ mt: 2 }} />
                    <TextField
                        label="Content"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        fullWidth required multiline rows={4} sx={{ mt: 2 }}
                    />

                    {/* Live Markdown Preview */}
                    {content && (
                        <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: '#fafafa' }}>
                            <Typography variant="subtitle2" gutterBottom>Live Preview:</Typography>
                            <ReactMarkdown linkTarget="_blank">
                                {content}
                            </ReactMarkdown>
                        </Box>
                    )}

                    <TextField label="Last Updated By" value="x-placeholder-user" fullWidth disabled sx={{ mt: 2 }} />
                    <TextField label="Timestamp" value="Will be set on submit" fullWidth disabled sx={{ mt: 2 }} />

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={9}>
                            <DateTimePicker
                                label="Valid From"
                                value={validFrom}
                                onChange={(newValue) => setValidFrom(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth required />}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl fullWidth><InputLabel>Timezone</InputLabel><Select defaultValue="PDT" label="Timezone">{timezones.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}</Select></FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={9}>
                            <DateTimePicker
                                label="Valid Till"
                                value={validTill}
                                onChange={(newValue) => setValidTill(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth required />}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl fullWidth><InputLabel>Timezone</InputLabel><Select defaultValue="PDT" label="Timezone">{timezones.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}</Select></FormControl>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            </LocalizationProvider>
        </Modal>
    );
}

